import { ChangeDetectorRef, Component, ViewChild, inject, DestroyRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatLegacyMenu as MatMenu } from '@angular/material/legacy-menu';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

import { noop, filter, tap } from 'rxjs';
import { Store } from '@ngxs/store';
import cloneDeep from 'lodash-es/cloneDeep';
import { guid } from '@shared/Utils/common.utils';
import { MenuItem } from './dashboard-header.types';
import { AccountState } from '@state/account/state';
import { Account } from '@core/models/account.types';
import { FeatureFlag, NewPermission, Permission } from '@core/enums';
import { UserService } from 'app/layout/common/user/user.service';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { Project, ProjectTemplate } from 'app/modules/projects/projects.types';
import { ConfirmService } from '@shared/components/widgets/dialog/confirm/confirm.service';
import { EditTabTitleModalComponent } from '@shared/modals/edit-tab-title-modal/edit-tab-title-modal.component';
import { UserState } from '@state/user/state';

@Component({
  selector: 'project-dashboard-header',
  templateUrl: './dashboard-header.component.html',
  styleUrls: ['./dashboard-header.component.scss'],
})
export class ProjectDashboardHeaderComponent {
  exportingTabAsPdf: boolean = false;
  editMode: boolean = false;
  account: Account;
  project: Project;
  userRole: string;
  projectTemplate: ProjectTemplate<any>;
  projectRoute: string;
  currentTabId: number;
  newPermission = NewPermission;

  private projectTemplateMemento: ProjectTemplate<any> = null;
  private destroyRef = inject(DestroyRef);

  @ViewChild('matMenu', { static: true }) matMenu: MatMenu;

  constructor(
    private readonly _projectsService: ProjectsService,
    private readonly _userService: UserService,
    private readonly _matDialog: MatDialog,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly confirmService: ConfirmService,
    private readonly store: Store,
  ) {
    this.account = this.store.selectSnapshot(AccountState.getAccount);
    this._activatedRoute.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.currentTabId = params.tab_id;
    });
  }

  ngOnInit(): void {
    this._projectsService.projectTemplate$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((projectTemplate: ProjectTemplate<any>) => {
        if (projectTemplate) {
          this.projectTemplate = projectTemplate;
          this.projectTemplateMemento = cloneDeep(projectTemplate);
          this._changeDetectorRef.markForCheck();
        }
      });

    this._projectsService.project$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((project: Project) => {
      this.project = project;
      this.setProjectRoute();
      this._changeDetectorRef.markForCheck();
    });

    this._userService.role$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((role: string) => {
      if (role) {
        this.userRole = role;
      }
      this._changeDetectorRef.markForCheck();
    });

    this._projectsService.changePanelState$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state) => {
      this.editMode = state;
      this._changeDetectorRef.markForCheck();
    });

    this._projectsService.exportPdfState$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state) => {
      this.exportingTabAsPdf = !!state;
      this._changeDetectorRef.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.toggleEditMode(null, true);
  }

  private setProjectRoute(): void {
    this.projectRoute = '/projects/' + this.project.pipeline.id + '/' + this.project.id + '/details';
  }

  private updateTabTitle(event: { id: string; displayText: string }, dialogResponse: string | undefined): void {
    const selectedTab = this.projectTemplate.definition.dashboard.tabs.filter(({ id }) => id === event.id);
    selectedTab[0].name = dialogResponse;
    this.cloneDashboardAndDetectChanges();
  }

  private cloneDashboardAndDetectChanges(): void {
    this.projectTemplate.definition.dashboard = cloneDeep(this.projectTemplate.definition.dashboard);
    this._changeDetectorRef.detectChanges();
  }

  public exportAsPdf(): void {
    this._projectsService.exportPdf(this.currentTabId || 'overview');
  }

  public toggleEditMode(event: Event, cancel: boolean): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (cancel) {
      this.projectTemplate = cloneDeep(this.projectTemplateMemento);
    }

    this.editMode = !this.editMode;
    this._projectsService.changePanelLayout(!cancel ? this.editMode : null);
  }

  public onTabOrderChanged(event: string[]): void {
    this.projectTemplate.definition.dashboard.header = event;
    this.cloneDashboardAndDetectChanges();
  }

  public onTabAdded(event: { tabName: string; menuItemKeys: string[] }): void {
    if (!this.projectTemplate.definition.dashboard.tabs) {
      this.projectTemplate.definition.dashboard.tabs = [];
    }

    const id = guid();

    this.projectTemplate.definition.dashboard.tabs.push({
      id: id,
      name: event.tabName,
      layout: [],
      panels: [],
    });

    this.projectTemplate.definition.dashboard.header = [...event.menuItemKeys, id];

    this.cloneDashboardAndDetectChanges();
  }

  public onTabRemoved(event: { id: string; displayText: string }): void {
    this.confirmService
      .openConfirmationModal({
        message: `Are you sure that you wish to remove the tab: ${event.displayText}?`,
      })
      .pipe(
        filter((res: boolean) => res),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.projectTemplate.definition.dashboard.tabs = this.projectTemplate.definition.dashboard.tabs.filter(
          ({ id }) => id !== event.id,
        );
        this.projectTemplate.definition.dashboard.header = this.projectTemplate.definition.dashboard.header.filter(
          (menuItemKey) => menuItemKey !== event.id,
        );

        this.cloneDashboardAndDetectChanges();
      });
  }

  public onTabTitleEdit(event: { id: string; displayText: string }): void {
    this._matDialog
      .open(EditTabTitleModalComponent, {
        data: {
          previousTabTitle: event.displayText,
        },
      })
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((newTabTitle: string) => {
          if (newTabTitle) {
            this.updateTabTitle(event, newTabTitle);
          }
        }),
      )
      .subscribe(noop);
  }

  public onTabDuplicated(menuItem: MenuItem): void {
    const copiedTab = {
      ...this.projectTemplate.definition.dashboard.tabs.find(({ id }) => id === menuItem.id),
      id: guid(),
      name: `${menuItem.displayText} (Copy)`,
    };

    this.projectTemplate = {
      ...this.projectTemplate,
      definition: {
        ...this.projectTemplate.definition,
        dashboard: {
          ...this.projectTemplate.definition.dashboard,
          header: [...this.projectTemplate.definition.dashboard.header, copiedTab.id],
          tabs: [...this.projectTemplate.definition.dashboard.tabs, copiedTab],
        },
      },
    };

    this.cloneDashboardAndDetectChanges();
  }

  public authorizeEditLayout(): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.DEALS_MANAGE_LAYOUT));
    }
    return (
      !this.editMode && this.store.selectSnapshot(UserState.hasPermission(Permission.PROJECT_DASHBOARD_EDIT_LAYOUT))
    );
  }
}
